import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useInfiniteQuery, useMutation as useTanstackMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError } from '../utils/api';
import { toast } from 'sonner';

/**
 * Generic data-fetching hook powered by TanStack Query.
 *
 * Drop-in replacement for the earlier useState/useEffect version — same API,
 * now with automatic caching, deduplication, stale-while-revalidate,
 * and background refetching.
 *
 * @param {string|null} url - API endpoint (relative to /api). Pass null to skip fetching.
 * @param {object} options
 * @param {Array} options.deps - Extra dependencies appended to the query key (default: [])
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 * @param {boolean} options.showErrorToast - Show toast on error (default: true)
 * @param {*} options.initialData - Initial data value (default: null)
 * @param {Function} options.transform - Optional transform applied to response.data
 * @param {number} options.staleTime - Override default stale time in ms
 * @param {number|false|Function} options.refetchInterval - Optional polling interval
 *
 * @returns {{ data, loading, fetching, error, refetch, refetchOrThrow, setData }}
 *
 * `loading` is true only until the first fetch settles for this hook instance,
 * even when `initialData` is provided. Subsequent background refetches and
 * query-key changes keep `loading` false so callers can show stale data
 * (stale-while-revalidate). Use `data === initialData` or a separate flag if
 * you need to distinguish "fetching after filter change" from "ready".
 *
 * @example
 *   const { data: accounts, loading, refetch } = useApiQuery('/accounts');
 *
 * @example
 *   const { data } = useApiQuery(`/accounts/${id}/notes`, {
 *     deps: [id],
 *     enabled: !!id,
 *   });
 */
export function useApiQuery(url, {
  deps = [],
  enabled = true,
  showErrorToast = true,
  initialData = null,
  transform = null,
  staleTime = undefined,
  refetchInterval = false,
} = {}) {
  const queryClient = useQueryClient();

  // Build a stable query key from the URL + caller-supplied deps
  const queryKey = url ? ['api', url, ...deps] : ['api', '__disabled__'];

  const {
    data: rawData,
    isLoading,
    isFetching,
    error: queryError,
    refetch: tanstackRefetch,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await api.get(url, { signal });
      return transform ? transform(response.data) : response.data;
    },
    enabled: enabled && !!url,
    initialData: initialData !== null ? initialData : undefined,
    staleTime,
    refetchInterval,
    meta: { showErrorToast },
  });

  // Error toast side-effect (TanStack Query v5 removed onError from useQuery).
  // Must be in useEffect — never trigger side-effects during render.
  const error = queryError ? parseApiError(queryError) : null;
  const lastToastedError = useRef(null);

  useEffect(() => {
    if (error && showErrorToast && queryError !== lastToastedError.current) {
      toast.error(error);
      lastToastedError.current = queryError;
    }
  }, [error, showErrorToast, queryError]);

  // Maintain backward-compatible `refetch` that returns the data directly
  const refetch = useCallback(async () => {
    const result = await tanstackRefetch();
    return result.data;
  }, [tanstackRefetch]);
  const refetchOrThrow = useCallback(async () => {
    const result = await tanstackRefetch({ throwOnError: true });
    return result.data;
  }, [tanstackRefetch]);

  // Maintain backward-compatible `setData` for optimistic updates
  const setData = useCallback(
    (updater) => {
      queryClient.setQueryData(queryKey, (prev) =>
        typeof updater === 'function' ? updater(prev) : updater
      );
    },
    // eslint-disable-next-line
    [queryClient, url, ...deps]
  );

  // Prefetch helper — warms the cache without subscribing. Useful for
  // hover/focus preloading on a trigger that opens a modal/page using the
  // same key. No-op if a fetch is already in flight or cache is fresh.
  const prefetch = useCallback(
    () => {
      if (!url) return;
      queryClient.prefetchQuery({
        queryKey,
        queryFn: async ({ signal }) => {
          const response = await api.get(url, { signal });
          return transform ? transform(response.data) : response.data;
        },
        staleTime,
      });
    },
    // eslint-disable-next-line
    [queryClient, url, transform, staleTime, ...deps]
  );

  // Track whether this hook instance has ever observed a settled fetch.
  // Required so `loading` is true on first fetch even when `initialData` is set
  // (TanStack treats initialData as immediately resolved, so isLoading is false).
  const hasResolved = useRef(false);
  if (!isFetching && rawData !== undefined) hasResolved.current = true;

  return {
    data: rawData ?? initialData,
    loading: isFetching && !hasResolved.current,
    fetching: isFetching,
    error,
    refetch,
    refetchOrThrow,
    setData,
    prefetch,
  };
}

/**
 * Hook for API mutations (POST, PUT, DELETE) with loading/error state,
 * powered by TanStack Query's useMutation.
 *
 * @param {'post'|'put'|'patch'|'delete'} method - HTTP method
 * @param {string} url - API endpoint (relative to /api)
 * @param {object} options
 * @param {string} options.successMessage - Toast message on success
 * @param {boolean} options.showErrorToast - Show toast on error (default: true)
 * @param {Function} options.onSuccess - Callback after success
 * @param {Array<string>} options.invalidateKeys - Query keys to invalidate on success
 *
 * @returns {{ mutate, loading, error }}
 *
 * @example
 *   const { mutate: createNote, loading } = useApiMutation('post', `/accounts/${id}/notes`, {
 *     successMessage: 'Note created',
 *     onSuccess: refetchNotes,
 *     invalidateKeys: ['/accounts'],
 *   });
 *   await createNote({ content: 'Hello' });
 */
export function useApiMutation(method, url, {
  successMessage = null,
  showErrorToast = true,
  onSuccess = null,
  invalidateKeys = [],
} = {}) {
  const queryClient = useQueryClient();

  const mutation = useTanstackMutation({
    mutationFn: async ({ body, urlOverride } = {}) => {
      const targetUrl = urlOverride || url;
      const response = method === 'delete'
        ? await api.delete(targetUrl)
        : await api[method](targetUrl, body);
      return response.data;
    },
    onSuccess: (data) => {
      if (successMessage) {
        toast.success(successMessage);
      }
      // Invalidate related queries so lists auto-refresh
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: ['api', key] });
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (err) => {
      if (showErrorToast) {
        toast.error(parseApiError(err));
      }
    },
  });

  // Backward-compatible mutate that accepts (body, urlOverride) positionally
  const mutate = useCallback(
    async (body = undefined, urlOverride = undefined) => {
      return mutation.mutateAsync({ body, urlOverride });
    },
    [mutation]
  );

  return {
    mutate,
    loading: mutation.isPending,
    error: mutation.error ? parseApiError(mutation.error) : null,
  };
}

/**
 * Generic infinite-scroll data-fetching hook powered by TanStack Query's
 * useInfiniteQuery. Use this for paginated lists where the user "Load more"s
 * through pages and previously-loaded pages stay visible.
 *
 * @param {object} options
 * @param {Array} options.queryKey - Stable identifier for the query (e.g. ['activities', accountId, filter])
 * @param {(pageParam: any) => string} options.buildUrl - Function that returns the URL for a given pageParam
 * @param {any} [options.initialPageParam=1] - First page param
 * @param {(lastPage: any, allPages: any[]) => any} options.getNextPageParam - Returns next page param or undefined to stop
 * @param {(page: any) => any[]} [options.extractItems] - Extracts the items array from a page (default: page.data)
 * @param {boolean} [options.enabled=true] - Whether to fetch immediately
 * @param {boolean} [options.showErrorToast=true] - Show toast on error
 * @param {number} [options.staleTime]
 *
 * @returns {{ items, pages, loading, refreshing, loadingMore, hasMore, fetchNextPage, refetch, setPages, error }}
 *
 * `loading` is true only until the FIRST page settles for this hook instance.
 * `refreshing` is true while re-fetching the first page after a queryKey change.
 * `loadingMore` is true while fetching subsequent pages.
 * `items` is the flattened array of all loaded items.
 * `setPages(updater)` writes to the cache. `updater` receives the array of
 *   raw API responses (one per page). The page shape matches what your
 *   queryFn returns — e.g. `pages.map(page => page.map(item => ...))` for an
 *   array-shaped response, or `pages.map(page => ({...page, data: page.data.map(...)}))`
 *   for an envelope-shaped response.
 *
 * @example
 *   const { items: activities, loading, loadingMore, hasMore, fetchNextPage } = useInfiniteApiQuery({
 *     queryKey: ['activities', accountId, filter],
 *     buildUrl: (page) => `/accounts/${accountId}/activities?page=${page}&page_size=20`,
 *     getNextPageParam: (lastPage, allPages) => lastPage.has_more ? allPages.length + 1 : undefined,
 *   });
 */
export function useInfiniteApiQuery({
  queryKey,
  buildUrl,
  initialPageParam = 1,
  getNextPageParam,
  extractItems = (page) => page?.data || [],
  enabled = true,
  showErrorToast = true,
  staleTime = undefined,
} = {}) {
  const queryClient = useQueryClient();
  const fullQueryKey = ['api-infinite', ...(queryKey || [])];

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: tanstackRefetch,
    error: queryError,
  } = useInfiniteQuery({
    queryKey: fullQueryKey,
    queryFn: async ({ pageParam, signal }) => {
      const response = await api.get(buildUrl(pageParam), { signal });
      return response.data;
    },
    initialPageParam,
    getNextPageParam,
    enabled,
    staleTime,
  });

  const error = queryError ? parseApiError(queryError) : null;
  const lastToastedError = useRef(null);

  useEffect(() => {
    if (error && showErrorToast && queryError !== lastToastedError.current) {
      toast.error(error);
      lastToastedError.current = queryError;
    }
  }, [error, showErrorToast, queryError]);

  // Track first-page resolution so `loading` mirrors useApiQuery semantics
  const hasResolved = useRef(false);
  if (!isFetching && data?.pages?.length) hasResolved.current = true;

  const refetch = useCallback(async () => {
    const result = await tanstackRefetch();
    return result.data;
  }, [tanstackRefetch]);

  // Stable ref for extractItems so callers can pass inline arrow functions
  // without retriggering the items memo on every parent render.
  const extractItemsRef = useRef(extractItems);
  extractItemsRef.current = extractItems;

  const items = useMemo(
    () => (data?.pages || []).flatMap((p) => extractItemsRef.current(p) || []),
    [data],
  );

  // Optimistic-update helper: pass a function (pages) => newPages where each
  // page is the raw API response. Useful for mark-read, status flips, etc.
  const setPages = useCallback(
    (updater) => {
      queryClient.setQueryData(fullQueryKey, (prev) => {
        if (!prev) return prev;
        const nextPages = typeof updater === 'function' ? updater(prev.pages || []) : updater;
        return { ...prev, pages: nextPages };
      });
    },
    // eslint-disable-next-line
    [queryClient, ...fullQueryKey],
  );

  return {
    items,
    pages: data?.pages || [],
    loading: isFetching && !isFetchingNextPage && !hasResolved.current,
    refreshing: isFetching && !isFetchingNextPage && hasResolved.current,
    loadingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    fetchNextPage,
    refetch,
    setPages,
    error,
  };
}

export default useApiQuery;
